import { inject } from '@angular/core';
import { defer, of } from 'rxjs';
import { InitThreadResponse, Interceptor, Suggestion, SuggestionsResponse } from '@panaya/ai-chat/domain';
import { AiChatSuggestionsProviderService } from './ai-chat-suggestions-provider.service';

/**
 * Creates a suggestions interceptor that handles suggestions from multiple response formats.
 *
 * Supports:
 * 1. Old server format: InitThreadResponse with commands array containing SuggestionsResponse
 * 2. New server format: Direct isSuggestionsResponse from /suggestions endpoint
 *
 * The interceptor intelligently detects the response structure and extracts suggestions accordingly.
 *
 * @returns Interceptor function that processes suggestions from any response type
 */
export function createSuggestionsInterceptor(): Interceptor<InitThreadResponse | SuggestionsResponse> {
    const providerService = inject(AiChatSuggestionsProviderService);

    return (response: InitThreadResponse | SuggestionsResponse) =>
        defer(() => {
            const suggestions = extractSuggestions(response);

            if (suggestions.length > 0) {
                providerService.setSuggestions(suggestions);
            }

            return of(response); // Returns unmodified response
        });
}

/**
 * Extracts suggestions from any response format.
 * Handles multiple response structures:
 * - Direct SuggestionsResponse: { type: 'suggestions', suggestions: Suggestion[] }
 * - Response with commands array: { commands: [...] } where commands may contain SuggestionsResponse
 * - InitThreadResponse: { thread_id: string, commands?: SuggestionsResponse[] }
 *
 * @param response - Any response that might contain suggestions
 * @returns Array of suggestion objects, or empty array if none found
 */
function extractSuggestions(response: InitThreadResponse | SuggestionsResponse): Suggestion[] {
    // Case 1: Direct SuggestionsResponse (new server format)
    if (isSuggestionsResponse(response)) {
        return response.suggestions || [];
    }

    // Case 2: Response has commands array (old server format or PromptResponse)
    if ('commands' in response && response.commands && Array.isArray(response.commands)) {
        const suggestionsCmd = response.commands.find(isSuggestionsResponse);
        return suggestionsCmd?.suggestions || [];
    }

    // No suggestions found
    return [];
}

/**
 * Type guard to check if an object is a SuggestionsResponse
 */
function isSuggestionsResponse(obj: unknown): obj is SuggestionsResponse {
    return (
        typeof obj === 'object' &&
        obj !== null &&
        'type' in obj &&
        obj.type === 'suggestions' &&
        'suggestions' in obj &&
        Array.isArray(obj.suggestions)
    );
}
